import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Archive, Download, ShieldAlert, ShieldCheck } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import {
  ANONYMISED_NAME,
  ARCHIVE_GRACE_DAYS,
  LIVE_CONFIRMATION_WORD,
  RECOMMENDED_DRY_RUNS,
  REMOVED_TEXT,
  RETENTION_DUE_SORTS,
  RETENTION_DUE_STATUS_LABELS,
  RETENTION_MAX_DAYS,
  RETENTION_MIN_DAYS,
  RETENTION_REASON_LABELS,
  retentionDueStatus,
  type ArchiveRunView,
  type RetentionDueSort,
  type RetentionDueStatus,
  type RetentionMode,
  type RetentionOverview,
  type RetentionPreviewRow,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip, type ChipTone } from '../../../components/Chip';
import { DataTable } from '../../../components/data-table/DataTable';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { Pager } from '../../../components/Pager';
import { pageCountOf } from '../../../components/pager-items';
import { Radio, RadioGroup } from '../../../components/Checkbox';
import { SkeletonLines } from '../../../components/Skeleton';
import { RETENTION_STATUS_TONES } from '../../../components/status-tones';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { TableSection } from '../../../components/TableSection';
import type { Tone } from '../../../components/Tone';
import { errorMessage } from '../../../lib/api-client';
import { rebasedDraft } from '../../../lib/draft-rebase';
import { formatDate, formatDateTime, formatNumber } from '../../../lib/format';
import { EMPTY_SELECTION, type SelectionState } from '../../../lib/selection';
import { useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { archiveFileHref, archiveMonthName, useRetention, useRunArchive, useSaveRetentionSettings, type RetentionDueView } from '../api/retention-api';
import styles from './RetentionPage.module.css';

/** Live, past the archive's grace: tonight's sweep anonymises them. */
const READY_TONE: Tone = 'urgent';
const FIRST_PAGE: RetentionDueView = { page: 1, sort: 'due', dir: 'asc' };

const STATUS_CHIP_TONES: Readonly<Record<RetentionDueStatus, ChipTone>> = {
  would_be_anonymised: 'neutral',
  past_due: 'warning',
  waiting_for_archive: 'warning',
  anonymised_tonight: 'danger',
};

export interface RetentionUrl {
  /** N-85 opens the archive it announced. */
  readonly archive?: string;
}

/**
 * S30 — candidate data retention (D15, F20.07, F7.14). Option A of the admin redesign
 * (`5-settings/retention.html`, "The life of a record"): the rule as a picture first — closes,
 * counting down, the monthly archive, 7 days' grace, anonymised — then what is removed and what is
 * kept, the settings and archives, and who is due. Nothing is ever deleted: people are anonymised,
 * and only after an archive and its grace. Admin only (`admin_config`).
 */
export function RetentionPage(): ReactNode {
  const canView = useCanView('admin_config');
  const search: RetentionUrl = useSearch({ from: '/app-layout/admin/retention' });
  const [view, setView] = useState<RetentionDueView>(FIRST_PAGE);
  const retention = useRetention(canView, view);
  const data = retention.data;
  const hasData = data !== undefined;

  useEffect(() => {
    if (search.archive && hasData) document.getElementById(`archive-${search.archive}`)?.scrollIntoView({ block: 'center' });
  }, [search.archive, hasData]);

  if (!canView) return <ForbiddenState resource="retention" contact="Your admin" />;
  if (retention.error && !data) return <ServerErrorState onRetry={() => void retention.refetch()} />;
  if (!data) {
    return (
      <div className={styles.page}>
        <Card>
          <SkeletonLines count={8} />
        </Card>
      </div>
    );
  }
  return <Body data={data} highlighted={search.archive ?? null} view={view} onView={setView} />;
}

interface BodyProps {
  readonly data: RetentionOverview;
  readonly highlighted: string | null;
  readonly view: RetentionDueView;
  readonly onView: (view: RetentionDueView) => void;
}

function Body({ data, highlighted, view, onView }: BodyProps): ReactNode {
  const { counts } = data;
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>Retention</h1>
          <p className={styles.lead}>
            Candidates’ details are kept {formatNumber(data.settings.periodDays)} days after their last application closes — longer while a talent pool or another application holds them. Then they are anonymised: identity and CV go, every score, stage and date stays, so no report changes.
          </p>
        </div>
        <ModeChip mode={data.settings.mode} />
      </header>

      {/* Review W8: a grid of its own rather than the scrolling strip, so all five counts and their words show at 375. */}
      <Card isFlush>
        <dl className={styles.counts}>
          {[
            { label: 'Counting down', value: counts.counting },
            { label: 'Held by a pool', value: counts.pooled },
            { label: 'Applications in progress', value: counts.inProgress },
            { label: 'Kept permanently', value: counts.kept },
            { label: 'Anonymised so far', value: counts.anonymised },
          ].map((count) => (
            <div key={count.label} className={styles.countTile}>
              <dt className={styles.countLabel}>{count.label}</dt>
              <dd className={styles.countValue}>{formatNumber(count.value)}</dd>
            </div>
          ))}
        </dl>
      </Card>

      <LifeOfARecord periodDays={data.settings.periodDays} />

      <div className={styles.grid}>
        <SettingsCard data={data} />
        <ArchivesCard data={data} highlighted={highlighted} />
      </div>

      <DueTable data={data} view={view} onView={onView} />
    </div>
  );
}

function ModeChip({ mode }: { readonly mode: RetentionMode }): ReactNode {
  return mode === 'live' ? <Chip tone="danger">Live — anonymising</Chip> : <Chip tone="neutral">Dry run — nothing removed</Chip>;
}

// --- The rule as a picture -----------------------------------------------------------------------

/**
 * "What happens to one person's record", from the real numbers: the period and the grace come from the
 * settings and `ARCHIVE_GRACE_DAYS`, so the picture changes when the period does. Then "Removed" and
 * "Kept" side by side, which answers "do we delete candidates?" plainly: no.
 */
function LifeOfARecord({ periodDays }: { readonly periodDays: number }): ReactNode {
  const steps: readonly { readonly mark: string; readonly title: string; readonly body: string }[] = [
    { mark: '1', title: 'Last application closes', body: 'Or they leave a pool, or a record was made and never used.' },
    { mark: formatNumber(periodDays), title: 'Days counting down', body: 'Paused while a pool or another application holds them.' },
    { mark: '1st', title: 'Listed in the monthly archive', body: 'An Excel file and their CVs, emailed to every admin.' },
    { mark: formatNumber(ARCHIVE_GRACE_DAYS), title: 'Days’ grace', body: 'Keep someone from their profile to stop it.' },
    { mark: '✓', title: 'Anonymised', body: 'Only in live mode. Scores, stages and dates stay.' },
  ];
  return (
    <Card title="What happens to one person’s record" action={<Chip tone="success">Nothing is ever deleted</Chip>}>
      <ol className={styles.timeline}>
        {steps.map((step, index) => (
          <li key={step.title} className={styles.step} data-last={index === steps.length - 1 || undefined}>
            <span className={styles.stepMark}>{step.mark}</span>
            <span className={styles.stepTitle}>{step.title}</span>
            <span className={styles.stepBody}>{step.body}</span>
          </li>
        ))}
      </ol>
      <div className={styles.pair}>
        <div className={`${styles.box} ${styles.removed}`}>
          <h3 className={styles.boxTitle}>Removed</h3>
          <p className={styles.boxBody}>Name, contact details, answers, CV files and the text of their emails — replaced with “{REMOVED_TEXT}”.</p>
        </div>
        <div className={`${styles.box} ${styles.kept}`}>
          <h3 className={styles.boxTitle}>Kept</h3>
          <p className={styles.boxBody}>Every score, stage, date and event, under “{ANONYMISED_NAME}”, so every report stays the same.</p>
        </div>
      </div>
    </Card>
  );
}

// --- Settings ------------------------------------------------------------------------------------

function SettingsCard({ data }: { readonly data: RetentionOverview }): ReactNode {
  const save = useSaveRetentionSettings();
  const showToast = useToasts((state) => state.show);
  // Review W7: the settings the draft started from, and their version. The save is checked against that version, so
  // another Admin's save in between is refused rather than overwritten, and "changed" is measured from it.
  const [base, setBase] = useState(() => baseOf(data.settings));
  const [periodDays, setPeriodDays] = useState(String(base.periodDays));
  const [mode, setMode] = useState<RetentionMode>(base.mode);
  const [confirmation, setConfirmation] = useState('');
  const period = Number(periodDays);
  const periodError = Number.isInteger(period) && period >= RETENTION_MIN_DAYS && period <= RETENTION_MAX_DAYS ? undefined : `Between ${RETENTION_MIN_DAYS} and ${formatNumber(RETENTION_MAX_DAYS)} days.`;
  const isDirty = periodDays !== String(base.periodDays) || mode !== base.mode;
  // The save shows the new settings at once (with the old version), so only a newer version is someone else's save.
  const isNewer = data.settings.version !== base.version;
  const readsLikeTheirs = periodDays === String(data.settings.periodDays) && mode === data.settings.mode;
  if (isNewer && !save.isPending && (!isDirty || readsLikeTheirs)) {
    const latest = baseOf(data.settings);
    setBase(latest);
    setPeriodDays(String(latest.periodDays));
    setMode(latest.mode);
  }
  const isOutdated = isDirty && isNewer && !save.isPending;
  const isGoingLive = mode === 'live' && data.settings.mode !== 'live';
  const needsWord = isGoingLive && confirmation.trim() !== LIVE_CONFIRMATION_WORD;

  const submit = (): void => {
    if (periodError || needsWord || !isDirty || !data.can.edit || save.isPending) return;
    // While out of date, "Save mine over theirs": their settings are the new base, only what was changed here goes on top.
    const theirs = baseOf(data.settings);
    const next = isOutdated ? rebasedDraft({ periodDays: period, mode }, { periodDays: base.periodDays, mode: base.mode }, { periodDays: theirs.periodDays, mode: theirs.mode }) : { periodDays: period, mode };
    const version = isOutdated ? theirs.version : base.version;
    if (isOutdated) {
      setBase(theirs);
      setPeriodDays(String(next.periodDays));
      setMode(next.mode);
    }
    const isSwitchingOn = next.mode === 'live' && data.settings.mode !== 'live';
    save.mutate(
      { ...next, version, ...(isSwitchingOn ? { confirmation: confirmation.trim() } : {}) },
      {
        onSuccess: (overview) => {
          const latest = baseOf(overview.settings);
          setBase(latest);
          setPeriodDays(String(latest.periodDays));
          setMode(latest.mode);
          setConfirmation('');
          showToast({ tone: 'success', message: mode === 'live' ? 'Retention is live' : 'Retention settings saved', detail: mode === 'live' ? `People are anonymised ${ARCHIVE_GRACE_DAYS} days after the archive that lists them.` : undefined });
        },
        onError: (error) => showToast({ tone: 'danger', message: "The settings weren't saved", detail: errorMessage(error) }),
      },
    );
  };

  return (
    <Card title="Settings" meta={dryRunsLine(data.dryRunsCompleted)}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            submit();
          }
        }}
      >
        <Field label="Keep details for" hint={`Days after the last application closes · ${RETENTION_MIN_DAYS} to ${formatNumber(RETENTION_MAX_DAYS)}`} isRequired error={periodError}>
          {({ inputId, describedBy }) => <Input id={inputId} aria-describedby={describedBy} inputMode="numeric" suffix="days" value={periodDays} disabled={!data.can.edit} onChange={(event) => setPeriodDays(event.target.value.replace(/\D/g, ''))} />}
        </Field>
        <fieldset className={styles.modes}>
          <legend className={styles.legend}>Mode</legend>
          <RadioGroup>
            <Radio name="retention-mode" value="dry_run" checked={mode === 'dry_run'} disabled={!data.can.edit} onChange={() => setMode('dry_run')} label="Dry run — list who would be anonymised; remove nothing" />
            <Radio name="retention-mode" value="live" checked={mode === 'live'} disabled={!data.can.edit} onChange={() => setMode('live')} label={`Live — anonymise people ${ARCHIVE_GRACE_DAYS} days after the archive that lists them`} />
          </RadioGroup>
        </fieldset>
        {isGoingLive && (
          <div className={styles.warning} role="alert">
            <ShieldAlert size={16} aria-hidden />
            <div>
              <p className={styles.warningTitle}>Anonymising cannot be undone</p>
              <p className={styles.meta}>
                Names, contact details, answers, CVs and the text of their emails are replaced for good. Type {LIVE_CONFIRMATION_WORD} to switch it on.
                {data.dryRunsCompleted < RECOMMENDED_DRY_RUNS ? ` Only ${data.dryRunsCompleted} dry run so far.` : ''}
              </p>
              <Field label={`Type ${LIVE_CONFIRMATION_WORD}`}>
                {({ inputId }) => <Input id={inputId} autoComplete="off" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} />}
              </Field>
            </div>
          </div>
        )}
        {data.can.edit && isOutdated && (
          <p className={styles.outdated} role="status">
            Someone else saved the retention settings while you were editing. Save puts only your changes on top of theirs; Discard shows theirs.
          </p>
        )}
        {data.can.edit && (
          <span className={styles.actions}>
            <Button type="submit" variant={isGoingLive ? 'danger' : 'solid'} isPending={save.isPending} disabled={!isDirty || periodError !== undefined || needsWord}>
              {isGoingLive ? 'Switch on live anonymisation' : isOutdated ? 'Save mine over theirs' : 'Save'}
            </Button>
            {isDirty && (
              <Button
                type="button"
                variant="ghost"
                disabled={save.isPending}
                onClick={() => {
                  const latest = baseOf(data.settings);
                  setBase(latest);
                  setPeriodDays(String(latest.periodDays));
                  setMode(latest.mode);
                  setConfirmation('');
                }}
              >
                Discard
              </Button>
            )}
          </span>
        )}
      </form>
    </Card>
  );
}

interface SettingsBase {
  readonly version: number;
  readonly periodDays: number;
  readonly mode: RetentionMode;
}

function baseOf(settings: RetentionOverview['settings']): SettingsBase {
  return { version: settings.version, periodDays: settings.periodDays, mode: settings.mode };
}

/** "1 of 2 recommended dry runs done…"; past two it stops counting against the recommendation. */
function dryRunsLine(done: number): string {
  if (done > RECOMMENDED_DRY_RUNS) return `${formatNumber(done)} dry runs done (${RECOMMENDED_DRY_RUNS} recommended before going live)`;
  return `${done} of ${RECOMMENDED_DRY_RUNS} recommended dry runs done before going live`;
}

// --- Archives ------------------------------------------------------------------------------------

function ArchivesCard({ data, highlighted }: { readonly data: RetentionOverview; readonly highlighted: string | null }): ReactNode {
  const run = useRunArchive();
  const showToast = useToasts((state) => state.show);
  const runNow = (): void =>
    run.mutate(undefined, {
      onSuccess: (archive) =>
        showToast(
          archive.status === 'completed'
            ? { tone: 'success', message: `Archive for ${archiveMonthName(archive.month)} sent`, detail: `${formatNumber(archive.records)} people, ${formatNumber(archive.documents)} files. Every admin has the Excel file by email.` }
            : { tone: 'danger', message: "The archive didn't finish", detail: archive.failureReason ?? 'Try again.' },
        ),
      onError: (error) => showToast({ tone: 'danger', message: "The archive didn't run", detail: errorMessage(error) }),
    });
  return (
    <Card
      title="Monthly archives"
      meta="Sent on the 1st to every admin: an Excel file of everyone due that month, and a zip with their CVs"
      action={
        data.can.edit ? (
          <Button variant="outline" size="sm" icon={Archive} isPending={run.isPending} onClick={runNow}>
            Run this month’s now
          </Button>
        ) : undefined
      }
    >
      {data.archives.length === 0 ? (
        <EmptyState isInset title="No archive yet" body="The first goes out on the 1st. Run this month’s now to see what it holds — in dry run nothing is removed." action={null} />
      ) : (
        <ul className={styles.archives}>
          {data.archives.map((archive) => (
            <ArchiveRow key={archive.id} archive={archive} isHighlighted={archive.id === highlighted} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function ArchiveRow({ archive, isHighlighted }: { readonly archive: ArchiveRunView; readonly isHighlighted: boolean }): ReactNode {
  return (
    <li id={`archive-${archive.id}`} className={isHighlighted ? `${styles.archive} ${styles.highlighted}` : styles.archive}>
      <div className={styles.archiveHead}>
        <span className={styles.name}>{archiveMonthName(archive.month)}</span>
        {archive.isDryRun ? <Chip tone="neutral">Dry run</Chip> : <Chip tone="danger">Live</Chip>}
        {archive.status === 'failed' && <Chip tone="danger">Failed</Chip>}
      </div>
      <p className={styles.meta}>
        {archive.status === 'failed'
          ? (archive.failureReason ?? 'It did not finish.')
          : `${formatNumber(archive.records)} people · ${formatNumber(archive.documents)} files${archive.sentAt ? ` · sent ${formatDateTime(archive.sentAt)}` : ''}${!archive.isDryRun && archive.graceEndsAt ? ` · nothing removed before ${formatDate(archive.graceEndsAt)}` : ''}${archive.anonymised > 0 ? ` · ${formatNumber(archive.anonymised)} anonymised` : ''}`}
      </p>
      <span className={styles.actions}>
        {archive.hasWorkbook && (
          <a className={styles.download} href={archiveFileHref(archive.id, 'workbook')} download>
            <Download size={14} aria-hidden /> Excel
          </a>
        )}
        {archive.hasZip && (
          <a className={styles.download} href={archiveFileHref(archive.id, 'zip')} download>
            <Download size={14} aria-hidden /> Excel and CVs (zip)
          </a>
        )}
      </span>
    </li>
  );
}

// --- Who is due ----------------------------------------------------------------------------------

function isDueSort(value: string | undefined): value is RetentionDueSort {
  return RETENTION_DUE_SORTS.some((sort) => sort === value);
}

/**
 * Who is due this month, as a table (DECISIONS-LOG 2026-10-06), paged and sorted on the server: in dry
 * run it holds everyone past their date, so it grows each month. A new order goes back to page 1.
 */
function DueTable({ data, view, onView }: Omit<BodyProps, 'highlighted'>): ReactNode {
  const navigate = useNavigate();
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const mode = data.settings.mode;
  const isLive = mode === 'live';
  const { page, pageSize, total } = data.duePage;
  const sort: readonly SortSpec[] = [{ columnId: view.sort, direction: view.dir }];
  const columns: readonly Column<RetentionPreviewRow>[] = [
    {
      id: 'candidate',
      header: 'Candidate',
      isIdentity: true,
      isSortable: true,
      defaultWidth: 220,
      cell: (row) => (
        <Link to="/candidates/$candidateId" params={{ candidateId: row.candidateId }} className={styles.rowLink}>
          {row.name}
        </Link>
      ),
      exportValue: (row) => row.name,
    },
    { id: 'reason', header: 'Countdown started', isSortable: true, defaultWidth: 220, cell: (row) => RETENTION_REASON_LABELS[row.reason] },
    { id: 'from', header: 'Since', isSortable: true, defaultWidth: 120, cell: (row) => formatDate(row.from) },
    { id: 'due', header: 'Due', isSortable: true, defaultWidth: 120, cell: (row) => formatDate(row.dueAt) },
    { id: 'files', header: 'Files', isNumeric: true, isSortable: true, defaultWidth: 90, cell: (row) => formatNumber(row.documents) },
    {
      id: 'status',
      header: 'Status',
      isSortable: true,
      defaultWidth: 200,
      cell: (row) => {
        const status = retentionDueStatus(row, mode);
        return <Chip tone={STATUS_CHIP_TONES[status]}>{RETENTION_DUE_STATUS_LABELS[status]}</Chip>;
      },
      exportValue: (row) => RETENTION_DUE_STATUS_LABELS[retentionDueStatus(row, mode)],
    },
  ];

  // Review W20: ticks belong to the rows on screen. Another page or order shows other people, so they start clear.
  const go = (next: RetentionDueView): void => {
    setSelection(EMPTY_SELECTION);
    onView(next);
  };

  const changeSort = (next: readonly SortSpec[]): void => {
    const first = next[0];
    go({ page: 1, sort: isDueSort(first?.columnId) ? first.columnId : 'due', dir: first?.direction ?? 'asc' });
  };

  return (
    <TableSection
      title={`${isLive ? 'Due this month' : 'Due this month — dry run'} · ${formatNumber(total)}`}
      meta={isLive ? `Removed once the archive listing them is ${ARCHIVE_GRACE_DAYS} days old. Keep someone from their profile to stop it.` : 'Who would be anonymised this month, and why. Nothing happens in dry run.'}
    >
      {total === 0 ? (
        <EmptyState isInset title="Nobody is due this month" body="Everyone is either still in a process, held by a talent pool, kept, or not yet past their date." action={null} />
      ) : (
        <>
          <DataTable
            screenId="retention-due"
            label="Candidates due this month"
            columns={columns}
            rows={data.dueThisMonth}
            toneOf={(row) => (isLive && row.isReady ? READY_TONE : RETENTION_STATUS_TONES.due)}
            getRowId={(row) => row.candidateId}
            // No `total`: the table holds one page, so "Select all N matching" would export only that page (review W20).
            sort={sort}
            onSortChange={changeSort}
            selection={selection}
            onSelectionChange={setSelection}
            onOpenRow={(row) => void navigate({ to: '/candidates/$candidateId', params: { candidateId: row.candidateId } })}
            linkOf={(row) => `/candidates/${row.candidateId}`}
            exportName="retention-due-this-month"
          />
          <Pager page={page} pageCount={pageCountOf(total, pageSize)} pageSize={pageSize} rowCount={data.dueThisMonth.length} total={total} onPage={(next) => go({ ...view, page: next })} />
        </>
      )}
      {data.overdue > 0 && (
        <p className={styles.meta}>
          <ShieldCheck size={14} aria-hidden /> {formatNumber(data.overdue)} past their date, waiting for an archive and its grace{isLive ? '' : ' — and for live mode'}.
        </p>
      )}
    </TableSection>
  );
}
